import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * @typedef {object} BuyItTogetherCardRefs
 * @property {HTMLSelectElement} variantSelect
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} error
 *
 * @extends Component<BuyItTogetherCardRefs>
 */
class BuyItTogetherCard extends Component {
  requiredRefs = ['variantSelect', 'addButton', 'error'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    this.refs.variantSelect.addEventListener('change', this.#onVariantChange, { signal });
    this.refs.addButton.addEventListener('click', this.#onAddClick, { signal });

    // The parent <slideshow-slides> calls preventDefault() on mousedown to
    // detect drag-to-scroll. That swallows the event the native <select>
    // needs to open its dropdown, and competes with button clicks. Stop
    // pointer/mouse events from bubbling out of interactive controls.
    const stopPointer = /** @param {Event} e */ (e) => e.stopPropagation();
    for (const el of [this.refs.variantSelect, this.refs.addButton]) {
      el.addEventListener('pointerdown', stopPointer, { signal });
      el.addEventListener('mousedown', stopPointer, { signal });
      el.addEventListener('touchstart', stopPointer, { signal, passive: true });
      el.addEventListener('click', stopPointer, { signal });
    }

    this.#syncButtonState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #syncButtonState = () => {
    const hasSelection = Boolean(this.refs.variantSelect.value);
    this.refs.addButton.disabled = !hasSelection;
  };

  #onVariantChange = () => {
    this.#hideError();
    this.#syncButtonState();
  };

  #onAddClick = async () => {
    const variantId = this.refs.variantSelect.value;
    if (!variantId) return;

    this.refs.addButton.disabled = true;
    this.refs.addButton.dataset.loading = 'true';
    this.#hideError();

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        this.#showError(data.message || Theme.translations?.cart_error || 'Could not add item');
        document.dispatchEvent(
          new CartErrorEvent(this.id, data.message, data.description, data.errors)
        );
        return;
      }

      let cart = null;
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (_) {
        // Non-fatal — drawer will still open with sections HTML.
      }

      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'buy-it-together',
          itemCount: 1,
          variantId,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[buy-it-together]', error);
      this.#showError(Theme.translations?.cart_error || 'Could not add item');
    } finally {
      this.refs.addButton.removeAttribute('data-loading');
      this.#syncButtonState();
    }
  };

  /** @param {string} message */
  #showError(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = false;
  }

  #hideError() {
    this.refs.error.textContent = '';
    this.refs.error.hidden = true;
  }
}

if (!customElements.get('buy-it-together-card')) {
  customElements.define('buy-it-together-card', BuyItTogetherCard);
}
